import React, { useState, useMemo } from "react";
import AdminLayout from "./AdminDashboardLayout";
import Button from "../../utils/Button";
import toast from "react-hot-toast";
import { useApiMutation } from "../../hooks/useMutation";
import { useApiQuery } from "../../hooks/useApiQuery";
import { ApiRequestOptions } from "../../utils/api";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

type ExportType = "VOTER_REGISTRATIONS" | "REGISTRATION_CHECKS" | "FIELD_ACTIVITY";

type ExportStatus = "REQUESTED" | "APPROVED" | "REJECTED";

type ExportRequest = {
  id: number;
  exportType: ExportType;
  status: ExportStatus;
  requestedBy: string;
  requestedAt: string;
  approvedAt: string | null;
};

type ExportRequestsResponse = {
  success: boolean;
  message: string;
  data: {
    content: ExportRequest[];
    empty: boolean;
    first: boolean;
    last: boolean;
    number: number;
    numberOfElements: number;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
      paged: boolean;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      unpaged: boolean;
    };
    size: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    totalElements: number;
    totalPages: number;
  };
};

function getStatusClasses(status: string) {
  const normalized = status?.toUpperCase();
  if (normalized === "APPROVED") return "bg-green-100 text-green-800 border-green-200";
  if (normalized === "REJECTED") return "bg-red-100 text-red-800 border-red-200";
  return "bg-yellow-100 text-yellow-800 border-yellow-200";
}

function formatDateString(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM dd, yyyy 'at' h:mm a");
  } catch {
    return dateString;
  }
}

export default function AdminExports() {
  const queryClient = useQueryClient();
  
  const [selectedRequest, setSelectedRequest] = useState<ExportRequest | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [size] = useState(10);

  const exportRequestsQuery = useApiQuery<ExportRequestsResponse>({
    queryKey: ["admin-data-exports", page, size],
    request: {
      method: "GET",
      url: "/data-exports",
      params: { page, size },
    } as ApiRequestOptions,
  });

  const approveMutation = useApiMutation({
    onMutate: () => {
      toast.loading("Processing approval...", { id: "approve-toast" });
    },
    onSuccess: () => {
      toast.success("Export request approved successfully", { id: "approve-toast" });
      queryClient.invalidateQueries({ queryKey: ["admin-data-exports"] });
      setIsDetailsModalOpen(false);
      setSelectedRequest(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to approve request", {
        id: "approve-toast",
      });
    },
  });

  const rejectMutation = useApiMutation({
    onMutate: () => {
      toast.loading("Processing rejection...", { id: "reject-toast" });
    },
    onSuccess: () => {
      toast.success("Export request rejected successfully", { id: "reject-toast" });
      queryClient.invalidateQueries({ queryKey: ["admin-data-exports"] });
      setIsRejectModalOpen(false);
      setIsDetailsModalOpen(false);
      setSelectedRequest(null);
      setRejectReason("");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to reject request", {
        id: "reject-toast",
      });
    },
  });

  const requests = exportRequestsQuery.data?.data?.content || [];
  const totalPages = exportRequestsQuery.data?.data?.totalPages || 1;
  const isLoading = exportRequestsQuery.isLoading;

  const openDetails = (request: ExportRequest) => {
    setSelectedRequest(request);
    setIsDetailsModalOpen(true);
  };

  const closeDetails = () => {
    setIsDetailsModalOpen(false);
    setSelectedRequest(null);
  };

  const handleApprove = async (requestId: number) => {
    await approveMutation.mutateAsync({
      method: "POST",
      url: `/data-exports/${requestId}/approve`,
      data: { approve: true },
    });
  };

  const openRejectModal = (request: ExportRequest) => {
    setSelectedRequest(request);
    setIsRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    setIsRejectModalOpen(false);
    setRejectReason("");
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }

    await rejectMutation.mutateAsync({
      method: "POST",
      url: `/data-exports/${selectedRequest.id}/reject`,
      data: { approve: false, reason: rejectReason.trim() },
    });
  };

  return (
    <AdminLayout role="SYSTEM ADMIN">
      <div className="bg-gray-50 rounded-xl min-h-screen p-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Export Requests</h2>
            <p className="text-sm text-gray-500">Manage and review data export requests from partners</p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              All Export Requests
            </h3>
            <p className="text-sm text-gray-500">
              {requests.length} requests found
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Export Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={6}>
                      Loading export requests...
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-500" colSpan={6}>
                      No export requests found.
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr
                      key={request.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => openDetails(request)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        #{request.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {request.exportType.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {request.requestedBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDateString(request.requestedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusClasses(
                            request.status
                          )}`}
                        >
                          {request.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          {request.status === "REQUESTED" && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprove(request.id);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                disabled={approveMutation.isPending}
                              >
                                {approveMutation.isPending ? "Processing..." : "Approve"}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRejectModal(request);
                                }}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                disabled={rejectMutation.isPending}
                              >
                                Reject
                              </button>
                            </>
                          )}
                          {request.status !== "REQUESTED" && (
                            <span className="text-xs text-gray-500">No actions</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="p-4 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-500">
                Page {page + 1} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    page <= 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
                  disabled={page <= 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  Previous
                </button>
                <button
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    page >= totalPages - 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                  }`}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Details Modal */}
        {isDetailsModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-black/50"
              onMouseDown={closeDetails}
            />
            <div
              className="relative z-10 w-[720px] max-w-[95%] bg-white rounded-xl shadow-2xl p-6 text-gray-900 border border-gray-200"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Export Request Details</h2>
                <button
                  type="button"
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-900 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Request ID</div>
                  <div className="text-sm font-medium text-gray-900">#{selectedRequest.id}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Export Type</div>
                  <div className="text-sm font-medium text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {selectedRequest.exportType.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Requested By</div>
                  <div className="text-sm font-medium text-gray-900">{selectedRequest.requestedBy}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Requested At</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateString(selectedRequest.requestedAt)}</div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</div>
                  <div className="mt-1">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusClasses(
                        selectedRequest.status
                      )}`}
                    >
                      {selectedRequest.status}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Approved At</div>
                  <div className="text-sm font-medium text-gray-900">{formatDateString(selectedRequest.approvedAt)}</div>
                </div>
              </div>

              {selectedRequest.status === "REQUESTED" && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="text-sm font-medium text-gray-900 mb-4">Actions</div>
                  <div className="flex gap-3">
                    <Button
                      variant="primary"
                      size="medium"
                      label="Approve Request"
                      additionalClassname="bg-green-600 hover:bg-green-700"
                      disabled={approveMutation.isPending}
                      onClick={() => handleApprove(selectedRequest.id)}
                    />
                    <Button
                      variant="secondary"
                      size="medium"
                      label="Reject Request"
                      additionalClassname="bg-red-600 hover:bg-red-700 text-white border-red-600"
                      disabled={rejectMutation.isPending}
                      onClick={() => openRejectModal(selectedRequest)}
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <Button
                  variant="tertiary"
                  size="medium"
                  label="Close"
                  onClick={closeDetails}
                />
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {isRejectModalOpen && selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center" aria-modal="true" role="dialog">
            <div
              className="absolute inset-0 bg-black/50"
              onMouseDown={closeRejectModal}
            />
            <div
              className="relative z-10 w-[500px] max-w-[95%] bg-white rounded-xl shadow-2xl p-6 text-gray-900 border border-gray-200"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Reject Export Request</h2>
                <button
                  type="button"
                  onClick={closeRejectModal}
                  className="text-gray-500 hover:text-gray-900 transition-colors"
                >
                  ✕
                </button>
              </div>

              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">
                  Request #{selectedRequest.id} from {selectedRequest.requestedBy}
                </div>
                <div className="text-sm text-gray-600">
                  Export Type: <span className="font-medium">{selectedRequest.exportType.replace(/_/g, ' ')}</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Rejection <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={4}
                  placeholder="Please provide a reason for rejecting this export request..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="tertiary"
                  size="medium"
                  label="Cancel"
                  onClick={closeRejectModal}
                />
                <Button
                  variant="primary"
                  size="medium"
                  label="Reject Request"
                  additionalClassname="bg-red-600 hover:bg-red-700"
                  disabled={!rejectReason.trim() || rejectMutation.isPending}
                  onClick={handleReject}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
